import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { PublicCodeDetailsParams } from "../../../models";
import { searchProjects } from "../../../service/publicquery";
import { Status } from "../../../service/Shared";
import { useRouterContext } from "../../../useRouterContext";
import { useScrollToTop } from "../../../useScrollToTop";
import { getProjectPathnameForBackNavigation, getPublicProjectTypesAndStandardsRoute } from "../../../utils/routes";
import { DisplayHorizontalProject } from "../../../widget/cards/HorizontalProjectCards/models";

interface UsePublicCodeDetailsPageTemplateReturnData {
  standardUuid: string | undefined;
  organisationProjects: DisplayHorizontalProject[] | undefined;
  navigateBackToProject: () => void;
  navigateBackToProjectTypesAndStandards: () => void;
  showContactUsModal: boolean;
  setShowContactUsModal: Dispatch<SetStateAction<boolean>>;
}

export const usePublicCodeDetailsPageTemplate = (): UsePublicCodeDetailsPageTemplateReturnData => {
  useScrollToTop();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { standardUuid } = useParams<PublicCodeDetailsParams>();
  const { previousLocation } = useRouterContext();

  const [organisationProjects, setOrganisationProjects] = useState<DisplayHorizontalProject[] | undefined>();

  const fetchOrganisationProjects = useCallback(async (uuid: string) => {
    const response = await searchProjects({
      paging: {
        limit: 4,
        beforeCursor: null,
        afterCursor: null,
      },
      filter: {
        results: {
          standard: {
            uuid: { operator: "in", value: [uuid] },
          },
        },
      },
    });

    if (response.status === Status.Success) {
      const projects = response.data?.results.map((el) => ({
        uuid: el.uuid,
        imageSource: el.listingFiles?.length > 0 ? el.listingFiles[0]?.file?.url : null,
        displayName: el.displayName,
        addressCountryCode: el.addressCountryCode || "",
        developerSmallLogoFileUrl: el.developer.files.find((file) => file.type === "SmallLogo")?.file.url || null,
        developerDisplayName: el.developer?.displayName || undefined,
        codeName: el.standard.displayName || undefined,
      }));

      if (projects) {
        setOrganisationProjects(projects.slice(0, 5));
      }
    }
  }, []);

  useEffect(() => {
    if (standardUuid) {
      fetchOrganisationProjects(standardUuid);
    }
  }, [fetchOrganisationProjects, standardUuid]);

  const navigateBackToProject = (): void => {
    const projectUuid = searchParams.get("projectUuid");
    if (projectUuid) {
      navigate(getProjectPathnameForBackNavigation(projectUuid, previousLocation));
    }
  };

  const navigateBackToProjectTypesAndStandards = (): void => {
    navigate(getPublicProjectTypesAndStandardsRoute());
  };

  const [showContactUsModal, setShowContactUsModal] = useState(false);

  return {
    standardUuid,
    organisationProjects,
    navigateBackToProject,
    navigateBackToProjectTypesAndStandards,
    showContactUsModal,
    setShowContactUsModal,
  };
};
